import { useState, useEffect, useCallback } from 'react';
import { useProviderAuth, providerApi } from '../../contexts/ProviderAuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { 
  Building2, MapPin, Phone, Mail, Globe, 
  Save, Send, Loader2, CheckCircle, Clock, AlertCircle,
  Plus, X
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  draft: { label: 'Bozza', color: 'bg-gray-100 text-gray-700', icon: AlertCircle },
  submitted: { label: 'In Revisione', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approvato', color: 'bg-green-100 text-green-700', icon: CheckCircle }
};

export function ProfileEditor() {
  const { user } = useProviderAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Reference data
  const [macroAreas, setMacroAreas] = useState([]);
  const [comuni, setComuni] = useState([]);
  const [providerTypes, setProviderTypes] = useState({ types: [], subtypes: [], price_bands: [] });
  const [amenitiesOptions, setAmenitiesOptions] = useState([]);
  const [goodForOptions, setGoodForOptions] = useState([]);
  const [stylesOptions, setStylesOptions] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    business_name: '',
    type: '',
    subtype: '',
    comune: '',
    macro_area: '',
    short_description: '',
    long_description: '',
    services_offered: [],
    contact_email: '',
    contact_phone: '',
    price_band: '',
    address: '',
    website: '',
    amenities: [],
    good_for: [],
    styles: [],
    languages_spoken: ['italiano'],
    family_friendly: false,
    accessibility_info: '',
    operational_notes: '',
    availability_lead_time: '',
    operating_hours: '',
    special_services: []
  });

  // Service input for adding new services
  const [newService, setNewService] = useState('');
  const [newSpecialService, setNewSpecialService] = useState('');

  // Fetch reference data
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        const [areas, comuniData, types, amenities, goodFor, styles] = await Promise.all([
          providerApi('/provider/reference/macro-areas'),
          providerApi('/provider/reference/comuni'),
          providerApi('/provider/reference/types'),
          providerApi('/provider/reference/amenities'),
          providerApi('/provider/reference/good-for'),
          providerApi('/provider/reference/styles')
        ]);
        
        setMacroAreas(areas.macro_areas || []);
        setComuni(comuniData.comuni || []);
        setProviderTypes(types);
        setAmenitiesOptions(amenities.amenities || []);
        setGoodForOptions(goodFor.good_for || []);
        setStylesOptions(styles.styles || []);
      } catch (err) {
        console.error('Failed to fetch reference data:', err);
      }
    };
    
    fetchReferenceData();
  }, []);

  // Fetch existing profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await providerApi('/provider/profile');
        setProfile(data);
        setFormData({
          business_name: data.business_name || '',
          type: data.type || '',
          subtype: data.subtype || '',
          comune: data.comune || '',
          macro_area: data.macro_area || '',
          short_description: data.short_description || '',
          long_description: data.long_description || '',
          services_offered: data.services_offered || [],
          contact_email: data.contact_email || user?.email || '',
          contact_phone: data.contact_phone || '',
          price_band: data.price_band || '',
          address: data.address || '',
          website: data.website || '',
          amenities: data.amenities || [],
          good_for: data.good_for || [],
          styles: data.styles || [],
          languages_spoken: data.languages_spoken || ['italiano'],
          family_friendly: data.family_friendly || false,
          accessibility_info: data.accessibility_info || '',
          operational_notes: data.operational_notes || '',
          availability_lead_time: data.availability_lead_time || '',
          operating_hours: data.operating_hours || '',
          special_services: data.special_services || []
        });
      } catch (err) {
        // Profile doesn't exist yet, use defaults
        if (user?.email) {
          setFormData(prev => ({ ...prev, contact_email: user.email }));
        }
        if (user?.business_name) {
          setFormData(prev => ({ ...prev, business_name: user.business_name }));
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const addService = () => {
    if (newService.trim()) {
      setFormData(prev => ({
        ...prev,
        services_offered: [...prev.services_offered, newService.trim()]
      }));
      setNewService('');
    }
  };

  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services_offered: prev.services_offered.filter((_, i) => i !== index)
    }));
  };

  const addSpecialService = () => {
    if (newSpecialService.trim()) {
      setFormData(prev => ({
        ...prev,
        special_services: [...prev.special_services, newSpecialService.trim()]
      }));
      setNewSpecialService('');
    }
  };

  const removeSpecialService = (index) => {
    setFormData(prev => ({
      ...prev,
      special_services: prev.special_services.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (profile) {
        // Update existing
        const updated = await providerApi('/provider/profile', {
          method: 'PUT',
          body: JSON.stringify(formData)
        });
        setProfile(updated);
        toast.success('Profilo salvato!');
      } else {
        // Create new
        const created = await providerApi('/provider/profile', {
          method: 'POST',
          body: JSON.stringify(formData)
        });
        setProfile(created);
        toast.success('Profilo creato!');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitForApproval = async () => {
    setSubmitting(true);
    try {
      const result = await providerApi('/provider/profile/submit', {
        method: 'POST'
      });
      setProfile(result.profile);
      toast.success('Profilo inviato per approvazione!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-umbria-olive" />
      </div>
    );
  }

  const status = profile?.content_status || 'draft';
  const StatusIcon = STATUS_CONFIG[status]?.icon || AlertCircle;
  const canEdit = status !== 'submitted';
  const canSubmit = status === 'draft' && profile;

  return (
    <div className="space-y-6" data-testid="profile-editor">
      {/* Status Banner */}
      {profile && (
        <Card className={`border-0 ${STATUS_CONFIG[status]?.color}`}>
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StatusIcon className="h-5 w-5" />
              <span className="font-medium">
                Stato: {STATUS_CONFIG[status]?.label}
              </span>
            </div>
            {status === 'submitted' && (
              <span className="text-sm">
                Il tuo profilo è in fase di revisione. Riceverai una notifica quando sarà approvato.
              </span>
            )}
            {profile?.rejection_reason && (
              <span className="text-sm text-red-600">
                Motivo rifiuto: {profile.rejection_reason}
              </span>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Informazioni Attività
          </CardTitle>
          <CardDescription>
            I campi con * sono obbligatori
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Nome Attività *</Label>
              <Input
                id="business_name"
                value={formData.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                placeholder="Es. Agriturismo Il Poggio"
                disabled={!canEdit}
                data-testid="profile-business-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Tipologia *</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => handleChange('type', v)}
                disabled={!canEdit}
              >
                <SelectTrigger data-testid="profile-type-select">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {providerTypes.types.map(t => (
                    <SelectItem key={t} value={t}>
                      {t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtype">Sottotipologia *</Label>
              <Select 
                value={formData.subtype} 
                onValueChange={(v) => handleChange('subtype', v)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona sottotipologia" />
                </SelectTrigger>
                <SelectContent>
                  {providerTypes.subtypes.map(t => (
                    <SelectItem key={t} value={t}>
                      {t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="price_band">Fascia Prezzo *</Label>
              <Select 
                value={formData.price_band} 
                onValueChange={(v) => handleChange('price_band', v)}
                disabled={!canEdit}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona fascia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="economico">Economico</SelectItem>
                  <SelectItem value="medio">Medio</SelectItem>
                  <SelectItem value="alto">Alto</SelectItem>
                  <SelectItem value="luxury">Luxury</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Location */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <MapPin className="h-4 w-4" /> Localizzazione
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="macro_area">Macro Area *</Label>
                <Select 
                  value={formData.macro_area} 
                  onValueChange={(v) => handleChange('macro_area', v)}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona area" />
                  </SelectTrigger>
                  <SelectContent>
                    {macroAreas.map(area => (
                      <SelectItem key={area} value={area}>{area}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="comune">Comune *</Label>
                <Input
                  id="comune"
                  value={formData.comune}
                  onChange={(e) => handleChange('comune', e.target.value)}
                  placeholder="Es. Assisi"
                  disabled={!canEdit}
                  list="comuni-list"
                />
                <datalist id="comuni-list">
                  {comuni.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="address">Indirizzo</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="Via Roma 1, 06081 Assisi (PG)"
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Description */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="short_description">
                Descrizione Breve * <span className="text-umbria-muted">(max 200 caratteri)</span>
              </Label>
              <Textarea
                id="short_description"
                value={formData.short_description}
                onChange={(e) => handleChange('short_description', e.target.value.slice(0, 200))}
                placeholder="Una frase che descrive la tua attività..."
                rows={2}
                disabled={!canEdit}
                data-testid="profile-short-desc"
              />
              <p className="text-xs text-umbria-muted text-right">
                {formData.short_description.length}/200
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="long_description">Descrizione Completa *</Label>
              <Textarea
                id="long_description"
                value={formData.long_description}
                onChange={(e) => handleChange('long_description', e.target.value)}
                placeholder="Descrivi in dettaglio la tua attività, cosa offri, cosa ti rende unico..."
                rows={5}
                disabled={!canEdit}
              />
            </div>
          </div>

          <Separator />

          {/* Contact */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Phone className="h-4 w-4" /> Contatti
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    className="pl-10"
                    disabled={!canEdit}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Telefono *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                  <Input
                    id="contact_phone"
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="+39 075 123456"
                    className="pl-10"
                    disabled={!canEdit}
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="website">Sito Web</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-3 h-4 w-4 text-umbria-muted" />
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://www.tuosito.it"
                    className="pl-10"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Services */}
          <div className="space-y-4">
            <Label>Servizi Offerti</Label>
            <div className="flex flex-wrap gap-2">
              {formData.services_offered.map((service, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {service}
                  {canEdit && (
                    <button onClick={() => removeService(i)} className="ml-1 hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  value={newService}
                  onChange={(e) => setNewService(e.target.value)}
                  placeholder="Aggiungi servizio..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                />
                <Button type="button" variant="outline" onClick={addService}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <Separator />

          {/* Amenities & Styles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label>Servizi / Amenities</Label>
              <div className="flex flex-wrap gap-2">
                {amenitiesOptions.map(amenity => (
                  <Badge
                    key={amenity}
                    variant={formData.amenities.includes(amenity) ? 'default' : 'outline'}
                    className={`cursor-pointer ${!canEdit ? 'pointer-events-none' : ''}`}
                    onClick={() => canEdit && handleArrayToggle('amenities', amenity)}
                  >
                    {amenity.replace(/_/g, ' ')}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Adatto a</Label>
              <div className="flex flex-wrap gap-2">
                {goodForOptions.map(option => (
                  <Badge
                    key={option}
                    variant={formData.good_for.includes(option) ? 'default' : 'outline'}
                    className={`cursor-pointer ${!canEdit ? 'pointer-events-none' : ''}`}
                    onClick={() => canEdit && handleArrayToggle('good_for', option)}
                  >
                    {option}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Stile / Atmosfera</Label>
              <div className="flex flex-wrap gap-2">
                {stylesOptions.map(style => (
                  <Badge
                    key={style}
                    variant={formData.styles.includes(style) ? 'default' : 'outline'}
                    className={`cursor-pointer ${!canEdit ? 'pointer-events-none' : ''}`}
                    onClick={() => canEdit && handleArrayToggle('styles', style)}
                  >
                    {style}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="family_friendly">Family Friendly</Label>
                <Switch
                  id="family_friendly"
                  checked={formData.family_friendly}
                  onCheckedChange={(v) => handleChange('family_friendly', v)}
                  disabled={!canEdit}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Additional Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="operating_hours">Orari di Apertura</Label>
              <Input
                id="operating_hours"
                value={formData.operating_hours}
                onChange={(e) => handleChange('operating_hours', e.target.value)}
                placeholder="Es. 9:00-18:00 Lun-Sab"
                disabled={!canEdit}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="availability_lead_time">Preavviso Richiesto</Label>
              <Input
                id="availability_lead_time"
                value={formData.availability_lead_time}
                onChange={(e) => handleChange('availability_lead_time', e.target.value)}
                placeholder="Es. 48 ore, 1 settimana"
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="accessibility_info">Accessibilità</Label>
              <Textarea
                id="accessibility_info"
                value={formData.accessibility_info}
                onChange={(e) => handleChange('accessibility_info', e.target.value)}
                placeholder="Informazioni sull'accessibilità per persone con disabilità..."
                rows={2}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="operational_notes">Note Operative</Label>
              <Textarea
                id="operational_notes"
                value={formData.operational_notes}
                onChange={(e) => handleChange('operational_notes', e.target.value)}
                placeholder="Altre informazioni utili per gli ospiti..."
                rows={2}
                disabled={!canEdit}
              />
            </div>
          </div>

          {/* Special Services */}
          <div className="space-y-4">
            <Label>Servizi Speciali</Label>
            <p className="text-sm text-umbria-muted">
              Es. babysitting, chef privato, transfer, noleggio e-bike
            </p>
            <div className="flex flex-wrap gap-2">
              {formData.special_services.map((service, i) => (
                <Badge key={i} variant="secondary" className="gap-1">
                  {service}
                  {canEdit && (
                    <button onClick={() => removeSpecialService(i)} className="ml-1 hover:text-red-500">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </Badge>
              ))}
            </div>
            {canEdit && (
              <div className="flex gap-2">
                <Input
                  value={newSpecialService}
                  onChange={(e) => setNewSpecialService(e.target.value)}
                  placeholder="Aggiungi servizio speciale..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialService())}
                />
                <Button type="button" variant="outline" onClick={addSpecialService}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {canEdit && (
        <div className="flex justify-end gap-4">
          <Button
            variant="outline"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-profile-btn"
          >
            {saving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvataggio...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" /> Salva Bozza</>
            )}
          </Button>
          
          {canSubmit && (
            <Button
              onClick={handleSubmitForApproval}
              disabled={submitting}
              className="bg-umbria-olive hover:bg-umbria-olive-dark"
              data-testid="submit-profile-btn"
            >
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Invio...</>
              ) : (
                <><Send className="mr-2 h-4 w-4" /> Invia per Approvazione</>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
